import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Send, CheckCircle2 } from "lucide-react";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "never");

export function AccessReportCard() {
  const [rep, setRep] = useState(null);
  const [sending, setSending] = useState(false);
  const load = () => api.get("/admin/access-report").then((r) => setRep(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/access-report/send");
      toast[r.data.sent ? "success" : "error"](r.data.sent ? `Report emailed to ${rep.alert_email}.` : `Email failed (${r.data.error}). The report is still visible here.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!rep) return null;
  const t = rep.totals;
  return (
    <div className="warm-card p-6" data-testid="access-report-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-700" /><h2 className="font-display text-lg font-bold text-stone-900">Access log — last 7 days</h2></div>
        <button data-testid="access-report-send" onClick={sendNow} disabled={sending} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-amber-700 disabled:opacity-50">
          <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Email me this now"}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center mb-4" data-testid="access-report-totals">
        {[["Record views", t.phi_reads], ["Failed sign-ins", t.failed_logins], ["Failed 2-step codes", t.mfa_failures], ["AI requests", t.ai_requests]].map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-stone-50 p-3"><div className="font-mono text-xl font-bold text-stone-900">{v}</div><div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{l}</div></div>
        ))}
      </div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1.5">Flags</div>
      {rep.flags.length === 0 ? (
        <p className="text-sm text-emerald-800 inline-flex items-center gap-1.5" data-testid="access-report-no-flags"><CheckCircle2 className="w-4 h-4" /> Nothing unusual this week.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="access-report-flags">
          {rep.flags.map((f, i) => (
            <li key={`${i}-${f.user}`} className="text-sm flex items-start gap-2">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${f.level === "high" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{f.level}</span>
              <span className="text-stone-800"><strong>{f.user}</strong> — {f.text}</span>
            </li>
          ))}
        </ul>
      )}
      {rep.readers.length > 0 && (
        <div className="mt-4 pt-3 border-t border-stone-100">
          <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1.5">Who viewed member records</div>
          <ul className="space-y-1 text-xs text-stone-700" data-testid="access-report-readers">
            {rep.readers.slice(0, 8).map((r) => (
              <li key={r.user_id}><strong>{r.name}</strong> · {r.reads} views · {r.distinct_clients}/{r.linked_clients} clients · {r.csv_exports} exports · <span className={r.mfa_enabled ? "text-emerald-700" : "text-red-700 font-semibold"}>MFA {r.mfa_enabled ? "on" : "off"}</span></li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[11px] text-stone-400 mt-3">Emailed every Monday 08:00 UTC to {rep.alert_email} · last sent {fmtDate(rep.last_sent?.generated_at)}{rep.last_sent && !rep.last_sent.sent ? " (failed)" : ""}. Counts only — no client information leaves the app.</p>
    </div>
  );
}
